import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router';
import LoadingScreen from '../../components/muiLegacy/LoadingScreen/LoadingScreen';
import { projectsApiService } from '../../graphql/projects/projectsApiService';
import { usersApiService } from '../../graphql/users/usersApiService';
import NotFoundErrorPage from '../../logic/errorHandling/NotFoundErrorPage';
import AnalysisPage from '../AnalysisPage/AnalysisPage';
import ContentPlanPage from '../ContentPlanPage/ContentPlanPage';
import DataManagementPage from '../DataManagementPage/DataManagementPage';
import ProgressPage from '../ProgressPage/ProgressPage';
import ProjectSettingsPage from '../ProjectSettingsPage/ProjectSettingsPage';
import { FrontendUserEventGq } from '../../graphql/users/queries/useFrontendLogUserEventGq';
import { QueuedItemStatus } from '../../graphql/projects/models/QueuedItemStatus';

export interface ProjectPageProps {
  id: number;
}

export enum ProjectTabChoices {
  Collect = 'collect',
  Manage = 'manage',
  Analyze = 'analyze',
  Plan = 'plan',
  Progress = 'progress',
  Settings = 'settings',
}

const ProjectPage: React.FC<ProjectPageProps> = ({ id }) => {
  // Log user event
  const logUserEventMutation = usersApiService.mutations.useLogFrontendUserEvent();
  useEffect(() => {
    logUserEventMutation({
      event: FrontendUserEventGq.VISIT_PROJECT,
      projectId: id,
    });
  }, [id]);

  // Polling
  const slowPollInterval = 30000;
  const fastPollInterval = 3000;
  const [pollInterval, setPollInterval] = useState(fastPollInterval);

  // Data
  const projectQuery = projectsApiService.queries.useGetProject({ id }, { pollInterval });
  const userQuery = usersApiService.queries.useGetCurrentUser();

  // Status indicator
  const projectStatusQuery = projectsApiService.queries.getProjectStatus(
    { projectId: id },
    { pollInterval: pollInterval }
  ); // The data is retrieved from cache in StatusIndicator.tsx ... We keep it here to have the same reload function

  useEffect(() => {
    const loadingTagGroups = projectQuery?.result?.tagGroups?.some(
      (tg) => tg.tagGenerationRequest?.status === QueuedItemStatus.PROCESSING
    );
    if (!!projectStatusQuery?.result?.loading || !!loadingTagGroups) {
      setPollInterval(fastPollInterval);
    } else {
      setPollInterval(slowPollInterval);
    }
  }, [projectStatusQuery.result?.loading]);

  const combinedReload = useCallback(
    () =>
      Promise.all([
        projectQuery.query.refetch(),
        userQuery.query.refetch(),
        projectStatusQuery.query.refetch(),
      ]),
    [id]
  );

  // Tab logic
  const match = useRouteMatch();

  if (
    projectQuery?.query?.called &&
    !projectQuery?.query?.loading &&
    projectQuery?.query?.data?.projects?.getProject?.project === null
  ) {
    return <NotFoundErrorPage />;
  }

  // Render
  if (!projectQuery.result || !projectQuery.result.tagGroups || !userQuery.result) {
    return <LoadingScreen />;
  }

  return (
    <Switch>
      <Route
        path={match.path + '/' + ProjectTabChoices.Settings + '/:menuItem?'}
        render={() => (
          <ProjectSettingsPage
            project={projectQuery.result}
            reload={combinedReload}
            user={userQuery.result}
            path={match.url + '/' + ProjectTabChoices.Settings}
          />
        )}
      />
      <Route
        path={match.path + '/' + ProjectTabChoices.Manage + '/fetchRankData'}
        render={() => (
          <DataManagementPage
            projectId={id}
            project={projectQuery.result}
            user={userQuery.result}
            reload={combinedReload}
            fetchRankData={true}
            pollInterval={pollInterval}
          />
        )}
      />

      <Route
        path={match.path + '/' + ProjectTabChoices.Manage}
        render={() => (
          <DataManagementPage
            projectId={id}
            project={projectQuery.result}
            user={userQuery.result}
            reload={combinedReload}
            pollInterval={pollInterval}
          />
        )}
      />

      <Route
        path={match.path + '/' + ProjectTabChoices.Analyze + '/:selectedAnalaysisId?'}
        render={() => (
          <AnalysisPage
            project={projectQuery.result}
            reload={combinedReload}
            path={match.url + '/' + ProjectTabChoices.Analyze}
            user={userQuery.result}
          />
        )}
      />
      <Route
        path={match.path + '/' + 'analyse' + '/:selectedAnalaysisId?'}
        render={() => (
          <AnalysisPage
            project={projectQuery.result}
            reload={combinedReload}
            path={match.url + '/' + ProjectTabChoices.Analyze}
            user={userQuery.result}
          />
        )}
      />

      <Route
        path={match.path + '/' + ProjectTabChoices.Plan + '/:taskId?'}
        render={() => (
          <ContentPlanPage
            project={projectQuery.result}
            reload={combinedReload}
            user={userQuery.result}
          />
        )}
      />
      <Route
        path={match.path + '/' + ProjectTabChoices.Progress}
        render={() => (
          <ProgressPage
            projectId={id}
            project={projectQuery.result}
            user={userQuery.result}
            reload={combinedReload}
          />
        )}
      />
      <Route
        exact
        path={match.path + '/'}
        render={() => <Redirect to={match.url + '/' + ProjectTabChoices.Manage} />}
      />
      <Route
        exact
        path={match.path}
        render={() => <Redirect to={match.url + '/' + ProjectTabChoices.Manage} />}
      />
      <Route path={'/:unknown'} render={() => <NotFoundErrorPage />} />
    </Switch>
  );
};

export default ProjectPage;
